/* Free gift with the first order: keeps the cart in line with the offer, on every page.

   Pricing is done by Shopify: one AUTOMATIC Buy X Get Y discount per brand ("Free gift with $20+ of <brand>"):
   $20+ of the brand's products -> the listed gift variants 100% off, one per order. No codes. Shopify never puts
   the gift in the cart by itself, so this script does:
   - when a brand's items reach the threshold (the brand's own "threshold_cents" in the config, else the shared one,
     x Shopify.currency.rate; it must equal the amount in that brand's discount) and its gift is not in the cart, it adds
     the first gift in the brand's list that /cart/add.js accepts (422 = sold out -> next one);
   - when the brand's items drop under the threshold, it takes the gift out;
   - any gift line that is not free (an extra unit, a gift Shopify did not discount) is taken out at once, and a
     brand whose gift did not turn free is not retried at that same amount. Nobody pays for a "free gift".
   A gift the shopper removes stays removed for this cart ("Add it back" on the offer blocks undoes that).
   Known returning customers (logged in, with orders) get no gift; gifts already in their cart are taken out.
   With the promo switched off (config "enabled": false) it only takes our gifts out.

   The cart must never reach checkout in a state the script has not seen:
   - "pending" = the theme's quantity debounce, the theme's own cart request in flight, our timer, our run.
     While pending, the express checkout buttons are dimmed and inert, and a "Check out" submit (click or Enter)
     waits until the cart is settled, then goes on by itself.
   - Enter in a cart quantity box does not submit the cart form (it would post straight to checkout): it applies
     the quantity through the theme instead.
   - While this script moves lines, the cart rows are inert: the theme's +/-/Remove act by line number, and adding
     the gift shifts the lines. The lock is a <style> in <head>, so the theme's re-render cannot strip it; it is
     lifted once the theme has redrawn the cart with the fresh HTML handed over in the update event.
   Also paints the offer blocks on the page ([data-gift-offer], snippets/gift-offer.liquid).

   Config: <script type="application/json" id="gift-auto-config"> from snippets/gift-auto.liquid.
   Lines it adds carry the visible property "Free gift" and hidden _gift (brand key), _gift_from=auto. */
(function () {
  'use strict';
  if (window.GiftAuto) return;
  var node = document.getElementById('gift-auto-config');
  if (!node) return;
  var CONF;
  try { CONF = JSON.parse(node.textContent); } catch (e) { return; }
  var cfg = CONF.cfg || {};
  var brands = cfg.brands || [];
  if (!brands.length) return;
  var ENABLED = cfg.enabled !== false;

  var base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var HEAD = { 'Content-Type': 'application/json', Accept: 'application/json' };
  var SRC = 'gift-auto';
  var STORE = 'gift-auto-v1';
  var PROMO_TITLE = 'free gift with'; /* start of the automatic discounts' titles (lower case) */
  var MAX_ADDS = 8;     /* gift adds per page view; removals are never limited */
  var MAX_WRITES = 60;  /* all writes per page view: a last stop against any loop */
  var adds = 0, writes = 0, busy = false, again = false, timer = null, retried = false;
  var themeDebounceUntil = 0, rowsLocked = false, writing = false, lockTicker = null;
  var last = null, lastState = null;

  function getCart() {
    return fetch(base + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { if (!r.ok) throw new Error('cart ' + r.status); return r.json(); });
  }
  function post(path, body) {
    if (writes >= MAX_WRITES) return Promise.reject(new Error('write limit'));
    writes++;
    return fetch(base + path, { method: 'POST', headers: HEAD, body: JSON.stringify(body) });
  }
  function postOk(path, body) {
    return post(path, body).then(function (r) { if (!r.ok) throw new Error(path + ' ' + r.status); return r; });
  }
  function brandBy(key) { for (var i = 0; i < brands.length; i++) if (brands[i].key === key) return brands[i]; return null; }
  /* The threshold is set in store dollars; a visitor on another market currency sees converted prices.
     A brand may have its own (a dearer gift needs a bigger basket); without one the shared threshold applies. */
  function ownCents(b) { var t = Number(b && b.threshold_cents); return t > 0 ? t : 0; } /* 0, "", "abc": no own one */
  function baseCents(b) { return ownCents(b) || Number(cfg.threshold_cents) || 0; }
  function minimum(b) {
    var r = window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate);
    return Math.round(baseCents(b) * (r > 0 ? r : 1));
  }
  function minLabel(b) {
    var own = CONF.minLabels && CONF.minLabels[b.key];
    if (own) return own;
    /* a brand with its own amount never borrows the shared label */
    return (!ownCents(b) && CONF.minLabel) || ('$' + Math.round(baseCents(b) / 100));
  }
  function money(cents, cur) {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur || 'USD' }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  }
  function track(name, data) {
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data || {}); } catch (e) {}
    try { if (typeof window.clarity === 'function') window.clarity('event', name); } catch (e) {}
  }

  /* Per-cart memory (a new cart after checkout starts clean). */
  function load(token) {
    var blank = { token: token, added: {}, declined: {}, nofree: {}, soldout: {} };
    try {
      var s = JSON.parse(localStorage.getItem(STORE) || 'null');
      return s && s.token === token ? s : blank;
    } catch (e) { return blank; }
  }
  function save(s) { try { localStorage.setItem(STORE, JSON.stringify(s)); } catch (e) {} }

  /* A brand in this cart: our gift lines (Shopify splits a gift with quantity 2 into a paid and a free line, both
     tagged), which of them cost money, whether one is free, whether the shopper's OWN line of a gift variant took the
     brand's free unit instead, and the subtotal of the brand's other items. */
  function promoAllocated(it) {
    return (it.line_level_discount_allocations || []).some(function (a) {
      return a.amount > 0 && a.discount_application && String(a.discount_application.title || '').toLowerCase().indexOf(PROMO_TITLE) === 0;
    });
  }
  function info(b, cart) {
    var lines = [], paid = [], free = false, otherFree = false, sub = 0, vendors = b.vendors || [];
    (cart.items || []).forEach(function (it) {
      if (it.properties && it.properties._gift === b.key) {
        lines.push(it);
        if (it.final_line_price === 0) free = true; else paid.push(it);
        return;
      }
      if (vendors.indexOf(it.vendor) === -1) return;
      if (promoAllocated(it)) otherFree = true;
      sub += it.final_line_price;
    });
    return { lines: lines, paid: paid, free: free, otherFree: otherFree && !free, sub: sub };
  }
  function giftLinesOf(cart) {
    return (cart.items || []).filter(function (it) { return it.properties && it.properties._gift; });
  }

  function addGift(b) {
    var gifts = b.gifts || [], i = 0;
    function next() {
      if (i >= gifts.length) return Promise.resolve(false);
      if (adds >= MAX_ADDS) return Promise.reject(new Error('add limit'));
      var g = gifts[i++];
      adds++;
      return post('cart/add.js', { items: [{ id: Number(g[1]), quantity: 1, properties: {
        'Free gift': 'with ' + minLabel(b) + '+ of ' + b.name, _gift: b.key, _gift_from: 'auto' } }] })
        .then(function (r) {
          if (r.ok) return true;
          if (r.status === 422) return next(); /* sold out (or not available here): try the next gift */
          throw new Error('add ' + r.status);
        });
    }
    return next();
  }
  function removeLines(lines) {
    if (!lines.length) return Promise.resolve();
    var updates = {};
    lines.forEach(function (l) { updates[l.key] = 0; });
    return postOk('cart/update.js', { updates: updates });
  }

  /* ---- Locks -------------------------------------------------------------------------------------------------- */
  var lockStyle = document.createElement('style');
  lockStyle.id = 'gift-auto-locks';
  document.head.appendChild(lockStyle);
  var ROWS_CSS = 'cart-items-component .cart-items__table-row,cart-items-component .cart-items__quantity,' +
    'cart-items-component .cart-items__remove{pointer-events:none!important}';
  var DIM_CSS = 'cart-items-component .cart-items__table{opacity:.6;transition:opacity .15s}';
  var EXPRESS_CSS = 'shopify-accelerated-checkout-cart,.additional-checkout-buttons,.shopify-payment-button' +
    '{pointer-events:none!important;opacity:.5;transition:opacity .15s}';

  function themeInFlight() { return !!document.querySelector('cart-items-component.cart-items-disabled'); }
  function pending() { return busy || !!timer || Date.now() < themeDebounceUntil || themeInFlight(); }
  function applyLocks() {
    var css = (rowsLocked ? ROWS_CSS : '') + (writing ? DIM_CSS : '') + (pending() ? EXPRESS_CSS : '');
    if (lockStyle.textContent !== css) lockStyle.textContent = css;
    if (pending() && !lockTicker) lockTicker = setInterval(applyLocks, 150);
    if (!pending() && lockTicker) { clearInterval(lockTicker); lockTicker = null; }
  }
  function whenIdle() {
    return new Promise(function (res) {
      var t0 = Date.now();
      (function check() {
        if (!pending() || Date.now() - t0 > 10000) return res();
        setTimeout(check, 120);
      })();
    });
  }

  function sectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (c) {
      if (ids.indexOf(c.dataset.sectionId) === -1) ids.push(c.dataset.sectionId);
    });
    return ids;
  }
  /* Hand the fresh cart HTML to the theme with the event, so it redraws at once. No Accept header: with
     "application/json" Shopify answers product/cart/page URLs with their own JSON instead of the sections.
     Resolves when the cart rows on the page show the new cart (or after a timeout). */
  function announce(cart) {
    var ids = sectionIds();
    var html = ids.length
      ? fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')))
          .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; })
      : Promise.resolve(null);
    return html.then(function (sections) {
      var complete = !!sections && ids.every(function (id) { return typeof sections[id] === 'string'; });
      var redrawn = ids.length && !complete ? waitForRedraw(3000) : null;
      var ev = new Event('cart:update', { bubbles: true });
      var data = { source: SRC, itemCount: cart.item_count };
      if (complete) data.sections = sections;
      ev.detail = { resource: cart, sourceId: SRC, data: data };
      try { document.dispatchEvent(ev); } catch (e) {}
      /* With the sections the theme morphs synchronously; without them it fetches: wait for that redraw. */
      return redrawn || new Promise(function (res) { setTimeout(res, 30); });
    });
  }
  function waitForRedraw(ms) {
    return new Promise(function (res) {
      var comps = document.querySelectorAll('cart-items-component');
      if (!comps.length || !window.MutationObserver) return setTimeout(res, 600);
      var done = false, obs = new MutationObserver(function () { if (!done) { done = true; obs.disconnect(); setTimeout(res, 50); } });
      comps.forEach(function (c) { obs.observe(c, { childList: true, subtree: true }); });
      setTimeout(function () { if (!done) { done = true; obs.disconnect(); res(); } }, ms);
    });
  }

  /* ---- The cart logic ----------------------------------------------------------------------------------------- */
  function run() {
    var changed = false;
    return getCart().then(function (cart) {
      var st = load(cart.token);
      var toAdd = [], toRemove = [];

      if (CONF.returning || !ENABLED) {
        toRemove = giftLinesOf(cart);
      } else {
        brands.forEach(function (b) {
          var x = info(b, cart);
          var min = minimum(b);
          if (!(min > 0)) { toRemove = toRemove.concat(x.lines); return; } /* no threshold: no gift for this brand */
          if (x.otherFree) { toRemove = toRemove.concat(x.lines); return; } /* the shopper's own gift variant got it */
          if (x.sub >= min) {
            if (x.lines.length) {
              st.added[b.key] = x.sub; /* the brand amount the gift was last seen with */
              if (x.free) toRemove = toRemove.concat(x.paid); /* extra paid units of a free gift */
              return;
            }
            /* Gift gone while the brand amount is unchanged: the shopper took it out. (If the amount moved too, the
               cart was rebuilt, e.g. cleared and refilled in one go: add it again.) */
            if (st.added[b.key] != null && st.added[b.key] === x.sub) { st.declined[b.key] = true; delete st.added[b.key]; return; }
            delete st.added[b.key];
            if (st.declined[b.key]) return;
            if (st.nofree[b.key] === x.sub || st.soldout[b.key] === x.sub) return; /* already tried at this amount */
            toAdd.push(b);
          } else {
            delete st.added[b.key];
            toRemove = toRemove.concat(x.lines);
          }
        });
      }

      save(st);
      var chain = Promise.resolve();
      if (toRemove.length || toAdd.length) { changed = true; writing = true; applyLocks(); }
      if (toRemove.length) chain = chain.then(function () { return removeLines(toRemove); });
      toAdd.forEach(function (b) {
        chain = chain.then(function () {
          return addGift(b).then(function (ok) {
            if (ok) { st.added[b.key] = info(b, cart).sub; track('gift_auto_add', { brand: b.key }); }
            else st.soldout[b.key] = info(b, cart).sub;
            save(st);
          });
        });
      });

      /* Check the real cart: no gift line may cost money. A brand left with no free gift is not retried at this amount. */
      chain = chain.then(function () {
        return (changed ? getCart() : Promise.resolve(cart)).then(function (c2) {
          var bad = giftLinesOf(c2).filter(function (l) { return l.final_line_price > 0; });
          if (!bad.length) return c2;
          brands.forEach(function (b) {
            var x = info(b, c2);
            if (x.paid.length && !x.free && !x.otherFree) { st.nofree[b.key] = x.sub; delete st.added[b.key]; }
          });
          save(st);
          changed = true;
          writing = true;
          applyLocks();
          return removeLines(bad).then(getCart);
        });
      });
      return chain.then(function (finalCart) { return { cart: finalCart, st: st, changed: changed }; });
    });
  }

  function sync() {
    clearTimeout(timer);
    timer = null;
    if (busy) { again = true; return; }
    busy = true;
    rowsLocked = true; /* from the first read: the rows must not act on lines this run is about to move */
    applyLocks();
    run().then(function (r) {
      retried = false;
      save(r.st);
      last = r.cart;
      lastState = r.st;
      paint();
      return r.changed ? announce(r.cart) : null;
    }, function () {
      /* A failed write leaves the cart unknown: show what is really there, and try once more a bit later. */
      if (!retried) { retried = true; setTimeout(schedule, 2000); }
      return getCart().then(function (c) { last = c; lastState = load(c.token); paint(); return announce(c); }, function () {});
    }).then(null, function () {}).then(function () {
      busy = false;
      rowsLocked = false;
      writing = false;
      applyLocks();
      if (again) { again = false; sync(); }
    });
  }
  function schedule() { clearTimeout(timer); timer = setTimeout(sync, 250); applyLocks(); }

  /* ---- Offer blocks on this page ------------------------------------------------------------------------------ */
  function paint() {
    var offers = document.querySelectorAll('[data-gift-offer]');
    if (!offers.length) return;
    offers.forEach(function (el) {
      var b = brandBy(el.dataset.key);
      var status = el.querySelector('[data-gift-status]'), back = el.querySelector('[data-gift-back]');
      if (!b || !status) return;
      var min = minimum(b);
      var x = last ? info(b, last) : { lines: [], free: false, otherFree: false, sub: 0 };
      var st = lastState || { declined: {}, soldout: {} };
      var msg = '', showBack = false;
      if (!ENABLED) msg = '';
      else if (CONF.returning) msg = 'This gift is for first orders.';
      else if (x.free) msg = '✓ In your cart, free';
      /* the shopper's own item took the brand's free unit (it is one of the gift variants): say so, not "this gift" */
      else if (x.otherFree) msg = '✓ Your free item from ' + b.name + ' is already in your cart.';
      else if (st.declined[b.key] && x.sub >= min) { msg = 'You removed this gift from your cart.'; showBack = true; }
      else if (st.soldout[b.key] != null && x.sub >= min) msg = 'This gift is out of stock right now.';
      else if (x.sub > 0 && x.sub < min) msg = 'Add ' + money(min - x.sub, last && last.currency) + ' more of ' + b.name + ' to get it.';
      el.classList.toggle('is-free', !!x.free);
      if (status.textContent !== msg) status.textContent = msg;
      if (back) back.hidden = !showBack;
    });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-gift-back]');
    if (!btn) return;
    var el = btn.closest('[data-gift-offer]');
    if (!el || !last) return;
    e.preventDefault();
    var st = load(last.token);
    delete st.declined[el.dataset.key];
    delete st.added[el.dataset.key];
    delete st.nofree[el.dataset.key];
    save(st);
    btn.hidden = true;
    var status = el.querySelector('[data-gift-status]');
    if (status) { status.textContent = 'Adding…'; status.setAttribute('tabindex', '-1'); status.focus({ preventScroll: true }); }
    sync();
  });

  /* ---- Checkout guards ---------------------------------------------------------------------------------------- */
  var releasing = false;
  /* Enter in a cart quantity box would submit the cart form, i.e. post the quantities straight to checkout. */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var t = e.target;
    if (!t || !t.matches || !t.matches('input[name="updates[]"], cart-items-component input[type="number"]')) return;
    e.preventDefault();
    themeDebounceUntil = Date.now() + 1500;
    applyLocks();
    t.blur(); /* the theme applies a typed quantity on blur */
  }, true);
  /* "Check out" (click or any submit of the cart form) waits while a change is pending, then goes on by itself. */
  document.addEventListener('submit', function (e) {
    if (releasing) return;
    var form = e.target;
    var sub = e.submitter;
    var toCheckout = (sub && sub.name === 'checkout') || (form && form.querySelector && form.querySelector('[name="checkout"]') &&
      String(form.getAttribute('action') || '').indexOf('/cart') !== -1);
    if (!toCheckout || !pending()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (sub) sub.setAttribute('aria-busy', 'true');
    whenIdle().then(function () {
      if (sub) sub.removeAttribute('aria-busy');
      releasing = true;
      try {
        if (form.requestSubmit) form.requestSubmit(sub && sub.form === form ? sub : undefined);
        else form.submit();
      } finally { releasing = false; }
    });
  }, true);
  document.addEventListener('click', function (e) {
    if (releasing || !pending()) return;
    var a = e.target.closest && e.target.closest('a[href$="/checkout"], a[href*="/checkout?"]');
    if (!a) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    whenIdle().then(function () { window.location.href = a.href; });
  }, true);

  /* ---- Triggers ----------------------------------------------------------------------------------------------- */
  document.addEventListener('quantity-selector:update', function () {
    themeDebounceUntil = Date.now() + 1500; /* the theme sends the change 300 ms later */
    applyLocks();
  });
  function changedElsewhere(e) {
    var d = e.detail || {};
    if (d.sourceId === SRC || (d.data && d.data.source === SRC)) return;
    themeDebounceUntil = 0;
    schedule();
  }
  document.addEventListener('cart:update', changedElsewhere);
  document.addEventListener('discount:update', changedElsewhere);
  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });

  window.GiftAuto = { sync: sync, pending: pending };
  if (Number(CONF.itemCount || 0) > 0) sync();
  else paint();
})();
